import {observer, Provider} from 'mobx-react';
import React, {useEffect, useMemo} from 'react';

import {AsyncPriceCourseCardContext} from '@udemy/browse-course';
import {DiscoveryUnit} from '@udemy/discovery-api';
import {FeatureVariantAssignment} from '@udemy/graphql';
import {
    CertificationExamVoucherBannerWrapper,
    CertificationOccupation,
    CertificationOccupationCarousel,
    VoucherUIRegion,
} from '@udemy/react-certification-preparation';
import {DiscoveryUnitsContainerStore} from '@udemy/react-discovery-units';
import {ContentArea} from '@udemy/react-layout-components';
import {CONTEXT_TYPES, ResourceContextMenuProvider} from '@udemy/resource-context-menu';
import {useUDData} from '@udemy/ud-data';

import {PersonalPlanBanner} from 'src/components/personal-plan-banner';

import {TopicPageExperimentVariants, TopicProps} from '../types/topic';
import {getPageObjectIdFromTopic} from '../utils';
import {
    AVAILABLE_UNIT_TYPES,
    isPersonalPlanMerchandisingUnit,
    isTopicSubscriptionUpsellBestsellerUnit,
} from './topic-discovery-units';
import {
    TopicDiscoveryUnitsContainerWrapper,
    TopicDiscoveryUnitsContainerWrapperLoader,
} from './topic-discovery-units-wrapper.react-component';
import {VideoCoursesFallbackCarousel} from './video-courses-fallback-carousel.react-component';

/** Discovery API context for cert topic below-the-fold units (backend-defined). */
export const CERT_TOPIC_DISCOVERY_CONTEXT = 'topic_static_cert_voucher';
/**
 * Registered page type in @udemy/discovery-api (`topic_static`) — used for label_id
 * mapping and API validation. The cert-specific discovery context is overridden
 * via fetchOptions.context.
 */
export const CERT_TOPIC_DISCOVERY_PAGE_TYPE = 'topic_static';
export const CERT_TOPIC_DISCOVERY_FETCH_OPTIONS = {
    pageSize: 50,
    context: CERT_TOPIC_DISCOVERY_CONTEXT,
};
const RESOURCE_CONTEXT_MENU_PROPS = {context: CONTEXT_TYPES.ALL_COURSES};

export const filterCertTopicDiscoveryUnit = (unit: Partial<DiscoveryUnit>) =>
    unit.type !== 'discovery_value_props';

export interface CertificationTopicDiscoverySectionProps
    extends Pick<
            TopicProps,
            'topic' | 'isConsumerSubsSubscriber' | 'isSubsLoading' | 'enrollmentStat'
        >,
        TopicPageExperimentVariants {
    isMobile: boolean;
    pageType: string;
    isTopicVoucherModuleAboveBundle?: FeatureVariantAssignment;
    certificationOccupations?: CertificationOccupation[];
    certificationName?: string;
}

export const CertificationTopicDiscoverySection = observer(
    (props: CertificationTopicDiscoverySectionProps) => {
        const {
            topic,
            enrollmentStat,
            isMobile,
            isConsumerSubsSubscriber,
            isSubsLoading,
            showPersonalPlanBadge,
            useVerticalRecoCourseCardStyle,
            threeCardVerticalLayoutExperimentIsEnabled,
            pageType,
            isTopicVoucherModuleAboveBundle,
            certificationOccupations,
            certificationName,
        } = props;

        const udData = useUDData();
        const isDiscoveryProviderReady = !isSubsLoading && !udData.isGlobalMeContextLoading;
        const showCertificationOccupation = udData.Config.price_country?.id === 'US';

        const mobxProviderProps = {
            isConsumerSubsSubscriber,
            showPersonalPlanBadge: showPersonalPlanBadge ?? false,
            showCodingExercisesBadge: false,
        };

        const verticalRecoCourseCardContextValue =
            useVerticalRecoCourseCardStyle === true
                ? {useCourseProductCard: true, hideCourseDescription: true}
                : {};

        const wrapTopicDiscoveryInContentArea =
            !!isMobile && !!threeCardVerticalLayoutExperimentIsEnabled;

        const pageObjectId = getPageObjectIdFromTopic(topic);

        const discoveryStore = useMemo(
            () =>
                new DiscoveryUnitsContainerStore({
                    pageType: CERT_TOPIC_DISCOVERY_PAGE_TYPE,
                    pageObjectId,
                }),
            [pageObjectId],
        );

        useEffect(
            function fetchCertTopicDiscoveryUnits() {
                if (!isDiscoveryProviderReady) {
                    return;
                }

                if (!discoveryStore.loading && discoveryStore.firstLoad && !discoveryStore.error) {
                    discoveryStore.fetchUnits(CERT_TOPIC_DISCOVERY_FETCH_OPTIONS);
                }
            },
            [discoveryStore, isDiscoveryProviderReady],
        );

        if (!isDiscoveryProviderReady || discoveryStore.firstLoad) {
            return <TopicDiscoveryUnitsContainerWrapperLoader isMobile={!!isMobile} />;
        }

        const videoCoursesUnit = discoveryStore.units.find(
            (unit) =>
                unit.type === AVAILABLE_UNIT_TYPES.BESTSELLER &&
                !isTopicSubscriptionUpsellBestsellerUnit(unit),
        );
        const showVideoCoursesFallback = !videoCoursesUnit?.items?.length;

        const filteredUnits = discoveryStore.units.filter(filterCertTopicDiscoveryUnit);
        const ppUpsellIndex = filteredUnits.findIndex(
            (unit) =>
                unit.type === AVAILABLE_UNIT_TYPES.PERSONAL_PLAN_MERCHANDISING ||
                isTopicSubscriptionUpsellBestsellerUnit(unit),
        );

        const personalPlanMerchandisingUnit = discoveryStore.units.find(
            isPersonalPlanMerchandisingUnit,
        );
        const personalPlanModuleHasCourses = (personalPlanMerchandisingUnit?.items ?? []).some(
            (item) => (item as {_class?: string})._class === 'course',
        );

        const isMXUser = !isConsumerSubsSubscriber && !udData.Config.brand.has_organization;
        const showPersonalPlanBannerFallback = isMXUser && !personalPlanModuleHasCourses;
        const examVoucherBannerUiRegion = isTopicVoucherModuleAboveBundle?.configuration
            ?.isTopicVoucherModuleAbovebundle
            ? ('above_course_bundle' as VoucherUIRegion)
            : ('below_course_bundle' as VoucherUIRegion);

        const topicDiscoveryUnits = (
            <TopicDiscoveryUnitsContainerWrapper
                topic={topic}
                enrollmentStat={enrollmentStat}
                isMobile={isMobile}
                store={discoveryStore}
                pageType={CERT_TOPIC_DISCOVERY_PAGE_TYPE}
                // TODO: hardcoded to 'general' until the cert-specific PP merchandising design in Figma is ready; switch back to 'cert' once it's implemented.
                ppMerchandisingVariant="general"
                discoveryUnitFilter={filterCertTopicDiscoveryUnit}
                fetchOptions={CERT_TOPIC_DISCOVERY_FETCH_OPTIONS}
            >
                <div
                    className="component-margin"
                    data-item-index={ppUpsellIndex !== -1 ? ppUpsellIndex : undefined}
                    key="exam-voucher-banner-and-occupation-carousel"
                >
                    <CertificationExamVoucherBannerWrapper
                        topicId={topic.id}
                        theme="dark"
                        layout="horizontal"
                        isTopicBanner
                        uiRegion={examVoucherBannerUiRegion}
                        ctaMode="buyNow"
                    />
                    {showCertificationOccupation && !!certificationOccupations?.length && (
                        <CertificationOccupationCarousel
                            certificationOccupations={certificationOccupations}
                            certificationName={certificationName ?? ''}
                        />
                    )}
                </div>
            </TopicDiscoveryUnitsContainerWrapper>
        );

        return (
            <Provider {...mobxProviderProps}>
                <ResourceContextMenuProvider resourceContextMenuProps={RESOURCE_CONTEXT_MENU_PROPS}>
                    <AsyncPriceCourseCardContext.Provider
                        value={verticalRecoCourseCardContextValue}
                    >
                        {showVideoCoursesFallback && (
                            <VideoCoursesFallbackCarousel
                                topicId={pageObjectId}
                                pageType={pageType}
                            />
                        )}
                        {wrapTopicDiscoveryInContentArea ? (
                            <ContentArea>{topicDiscoveryUnits}</ContentArea>
                        ) : (
                            topicDiscoveryUnits
                        )}
                        {showPersonalPlanBannerFallback && <PersonalPlanBanner />}
                    </AsyncPriceCourseCardContext.Provider>
                </ResourceContextMenuProvider>
            </Provider>
        );
    },
);
